<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'

import GroupShorterUrlForm from '@/components/frontend/shorter-list/form/GroupShorterUrlForm.vue'
import SingleShorterUrlForm from '@/components/frontend/shorter-list/form/SingleShorterUrlForm.vue'

import { useCheckPlanFeatures } from '@/composables/useCheckPlanFeature'

import * as aggr from '@/api-generator/aggr'
import { ShorterDialogMode } from '@/constant/client/shorterDialog'
import appStore from '@/stores'

import type {
  GroupUrlEditData,
  SingleUrlSubmitData,
  GroupUrlSubmitData,
  ShorterSingleFormModel,
  ShorterGroupFormModel,
  GroupData
} from '@/types/shorterUrlForm'

interface Props {
  mode: ShorterDialogMode
  editData?: ShorterSingleFormModel | null
  groupData?: GroupData | null
  groupEditData?: GroupUrlEditData | null
  onSubmit: (data: SingleUrlSubmitData | GroupUrlSubmitData, currentMode?: ShorterDialogMode) => void | Promise<void>
  onCancel: () => void
}

const props = defineProps<Props>()

const { canBatchCreateShortUrls } = useCheckPlanFeatures()

const orgSortTagStore = appStore.useOrgSortTagStore
const shortUrlStore = appStore.useShortUrlStore

const handleFormSubmit = async (data: SingleUrlSubmitData | GroupUrlSubmitData) => {
  await props.onSubmit(data, currentMode.value)
}

const TAB_NAME = {
  /** 個別建立 */
  SINGLE: 'single',
  /** 群組建立 */
  GROUP: 'group'
} as const

type TabName = (typeof TAB_NAME)[keyof typeof TAB_NAME]

// Tab 設定
interface TabConfig {
  name: TabName
  label: string
  disabled?: boolean
}

const dialogTabConfigs = ref<TabConfig[]>([
  {
    name: TAB_NAME.SINGLE,
    label: '個別建立',
    disabled: false
  },
  {
    name: TAB_NAME.GROUP,
    label: '群組建立',
    disabled: !canBatchCreateShortUrls.value
  }
])

const activeTabName = ref<TabName>(TAB_NAME.SINGLE)
const currentMode = ref(props.mode)

const isTabActive = (tabName: TabName) => {
  return activeTabName.value === tabName
}

// 判斷是否顯示建立 Tab
const showCreateTab = computed(() => {
  const createModes: ShorterDialogMode[] = [ShorterDialogMode.SINGLE_CREATE, ShorterDialogMode.GROUP_CREATE]
  return createModes.includes(props.mode)
})

// 判斷是否為個別模式
const isSingleMode = (mode: ShorterDialogMode) => {
  const singleModes: ShorterDialogMode[] = [
    ShorterDialogMode.SINGLE_CREATE,
    ShorterDialogMode.SINGLE_EDIT,
    ShorterDialogMode.SINGLE_COPY,
    ShorterDialogMode.SINGLE_CREATE_IN_GROUP,
    ShorterDialogMode.SINGLE_EDIT_IN_GROUP,
    ShorterDialogMode.SINGLE_COPY_IN_GROUP
  ]
  return singleModes.includes(mode)
}

// 判斷是否為群組模式
const isGroupMode = (mode: ShorterDialogMode) => {
  const groupModes: ShorterDialogMode[] = [ShorterDialogMode.GROUP_CREATE, ShorterDialogMode.GROUP_EDIT]
  return groupModes.includes(mode)
}

const showSingleForm = computed(() => isSingleMode(currentMode.value))
const showGroupForm = computed(() => isGroupMode(currentMode.value))

// 判斷是否為建立副本模式
const isCopyMode = computed(() => {
  const copyModes: ShorterDialogMode[] = [ShorterDialogMode.SINGLE_COPY, ShorterDialogMode.SINGLE_COPY_IN_GROUP]
  return copyModes.includes(props.mode)
})

// ==================== 表單資料初始化 ====================

/**
 * 創建單一短網址表單的預設資料
 */
const createSingleFormData = (options?: {
  editData?: ShorterSingleFormModel
  groupData?: GroupData
  subtagId?: number
}): ShorterSingleFormModel => {
  // 編輯模式或建立副本模式
  if (options?.editData) {
    return {
      ...options.editData,
      imageUrl: options.editData.thumbnailImageUrl,
      uploadImageBase64: '',
      directUrl: options.editData.urlCode || '',
      enableTraceTapeId: isCopyMode.value ? true : options.editData.enableTraceTapeId
    }
  }

  // 群組內建立模式
  if (options?.groupData || options?.subtagId !== undefined) {
    return {
      urlType: aggr.UrlType.Url,
      projectName: '',
      subtagId: options.groupData?.subtagId ?? options.subtagId,
      originalUrl: '',
      chineseTag: '',
      title: '',
      description: '',
      imageUrl: '',
      uploadImageBase64: '',
      directUrl: '',
      enableTraceTapeId: true
    }
  }

  // 一般建立模式（預設值）
  return {
    urlType: aggr.UrlType.Url,
    projectName: '',
    subtagId: undefined,
    originalUrl: '',
    chineseTag: '',
    title: '',
    description: '',
    imageUrl: '',
    uploadImageBase64: '',
    directUrl: '',
    enableTraceTapeId: true,
    orgTagId: undefined
  }
}

/**
 * 創建群組短網址表單的預設資料
 */
const createGroupFormData = (options?: { groupEditData?: GroupUrlEditData }): ShorterGroupFormModel => {
  if (options?.groupEditData) {
    return {
      urlGroupId: options.groupEditData.urlGroupId,
      projectName: options.groupEditData.projectName,
      subtagId: options.groupEditData.subtagId,
      title: options.groupEditData.title ?? null,
      description: options.groupEditData.description ?? null,
      enableTraceTapeId: options.groupEditData.enableTraceTapeId ?? true,
      thumbnailImageUrl: options.groupEditData.thumbnailImageUrl ?? null,
      orgTagId: options.groupEditData.orgTagId ?? null,
      useCsvFileUpload: false,
      csvData: undefined,
      uploadImageBase64: null
    }
  }

  return {
    projectName: null,
    subtagId: undefined,
    title: null,
    description: null,
    useCsvFileUpload: true,
    csvData: undefined,
    uploadImageBase64: null,
    enableTraceTapeId: true,
    orgTagId: undefined
  }
}

// 單一短網址表單資料
const singleFormData = ref<ShorterSingleFormModel>(createSingleFormData())

// 群組短網址表單資料
const groupFormData = ref<ShorterGroupFormModel>(createGroupFormData())

/**
 * 初始化單一短網址表單資料
 */
const initializeSingleFormData = () => {
  singleFormData.value = createSingleFormData({
    editData: props.editData ?? undefined,
    groupData: props.groupData ?? undefined
  })
}

/**
 * 初始化群組短網址表單資料
 */
const initializeGroupFormData = () => {
  groupFormData.value = createGroupFormData({
    groupEditData: props.groupEditData ?? undefined
  })
}

// Tab 切換處理
const handleTabClick = (tabName: TabName) => {
  const tab = dialogTabConfigs.value.find((t) => t.name === tabName)
  if (tab?.disabled) {
    return
  }

  activeTabName.value = tabName
}

// 監聽 Tab 變化，更新 mode
watch(activeTabName, (newVal) => {
  if (newVal === TAB_NAME.SINGLE) {
    currentMode.value = ShorterDialogMode.SINGLE_CREATE
  } else {
    currentMode.value = ShorterDialogMode.GROUP_CREATE
  }
})

const fetchOrgSortTags = async () => {
  await orgSortTagStore.fetchOrgSortTags({
    companyUuId: shortUrlStore.selectedCompanyUuId,
    companyOrganizationUuid: shortUrlStore.selectedOrganizationId
  })
}

// 初始化表單資料
onMounted(async () => {
  initializeSingleFormData()
  initializeGroupFormData()
  await fetchOrgSortTags()
})
</script>

<template>
  <div class="shorter-dialog-content">
    <div v-if="showCreateTab" class="mb-4 flex items-center justify-between rounded-4xl bg-[#f5f5f5] p-1">
      <div
        v-for="tab in dialogTabConfigs"
        :key="tab.name"
        class="flex w-1/2 items-center justify-center py-2 text-sm font-medium leading-5"
        :class="{
          'active-tab': isTabActive(tab.name),
          'cursor-not-allowed opacity-50': tab.disabled,
          'cursor-pointer': !tab.disabled
        }"
        @click="handleTabClick(tab.name)"
      >
        {{ tab.label }}
      </div>
    </div>
    <SingleShorterUrlForm
      v-if="showSingleForm"
      v-model="singleFormData"
      :mode="currentMode"
      :group-data="groupData ?? undefined"
      :on-submit="handleFormSubmit"
      :on-cancel="onCancel"
    />

    <GroupShorterUrlForm
      v-if="showGroupForm"
      v-model="groupFormData"
      :mode="currentMode"
      :on-submit="handleFormSubmit"
      :on-cancel="onCancel"
    />
  </div>
</template>

<style lang="scss" scoped>
.shorter-dialog-content {
  .active-tab {
    @apply rounded-[38px] bg-white text-app-brand-blue-500;
    box-shadow: 0 1.5px 30px 0 var(--color-blue-alpha-light-blue-5020, rgba(198, 215, 255, 0.2));
    backdrop-filter: blur(10px);
  }

  :deep(.el-input__wrapper) {
    font-size: 12px;
    letter-spacing: 1.2px;
  }

  :deep(.el-radio) {
    margin-right: 4px;
  }

  :deep(.el-radio__label) {
    letter-spacing: 1.2px;
    white-space: normal;
    line-height: 16px;
  }
}
</style>
